import { computed, inject } from '@angular/core'
import { AppRole } from '@models/enums'
import { ErrorMessage } from '@models/errorMessage.model'
import { ChartVariablePreference, User, WindSpeedUnitPreference } from '@models/user.model'
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals'
import { AuthService } from '@services/auth.service'
import { UserService } from '@services/user.service'
import { TranslateService } from '@ngx-translate/core'
import { ToastrService } from 'app/libs/toastr'
import { tap } from 'rxjs'
import { CompanyStore } from './company.store'

interface UserAccountState {
  user: User | undefined
  isLoading: boolean
  initial: boolean
  error?: ErrorMessage
}
const initialState: UserAccountState = {
  user: undefined,
  initial: true,
  isLoading: true,
}

const USER_PREFERENCES_STORAGE_KEY = 'agrodit.userPreferences'
const CHART_VARIABLE_PREFERENCE_VALUES: ChartVariablePreference[] = [
  'moisture',
  'salinity',
  'temperature',
  'salinityIndex',
]
const DEFAULT_CHART_VISIBLE_VARIABLES: ChartVariablePreference[] = ['moisture']
const WIND_SPEED_UNIT_VALUES: WindSpeedUnitPreference[] = ['kmh', 'ms']

const normalizeChartVisibleVariables = (
  variables: unknown,
): ChartVariablePreference[] => {
  if (!Array.isArray(variables)) return DEFAULT_CHART_VISIBLE_VARIABLES

  const normalized = variables.filter((v): v is ChartVariablePreference =>
    CHART_VARIABLE_PREFERENCE_VALUES.includes(v as ChartVariablePreference),
  )

  return normalized.length > 0
    ? Array.from(new Set(normalized))
    : DEFAULT_CHART_VISIBLE_VARIABLES
}

const normalizeWindSpeedUnit = (
  unit: unknown,
): WindSpeedUnitPreference =>
  WIND_SPEED_UNIT_VALUES.includes(unit as WindSpeedUnitPreference)
    ? (unit as WindSpeedUnitPreference)
    : 'kmh'

const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join(''),
  )

  return JSON.parse(jsonPayload)
}

export const UserAccountStore = signalStore(
  { protectedState: false, providedIn: 'root' },
  withState(initialState),
  withComputed(
    (
      { user },
      companyStore = inject(CompanyStore),
      authService = inject(AuthService),
    ) => ({
      companyAdminIds: computed(() => {
        const token = authService.token$()
        if (!token) return [] as number[]

        const raw = parseJwt(token)['companyAdmin']
        if (typeof raw !== 'string' || raw.trim().length === 0) {
          return [] as number[]
        }

        return raw
          .split(',')
          .map((id: string) => Number.parseInt(id, 10))
          .filter((id: number) => Number.isFinite(id))
      }),
      userAdmin: computed(() => user()?.appRole === AppRole.admin),
      userAdminOfSelectedCompany: computed(() => {
        if (!user()) return false
        if (user()?.appRole === AppRole.admin) return true

        const token = authService.token$()
        if (!token) return false
        const companyId = companyStore.selectedCompany()?.id

        return parseJwt(token)['companyAdmin'].includes(companyId)
      }),
      userComanyIds: computed(() => {
        if (!user()) return []
        return parseJwt(authService.token$()!)['companyUser'].split(',')
      }),
    }),
  ),
  withMethods(
    (
      store,
      users$ = inject(UserService),
      toastr$ = inject(ToastrService),
      translate = inject(TranslateService),
    ) => ({
      resetSession() {
        patchState(store, {
          user: undefined,
          isLoading: false,
          initial: true,
          error: undefined,
        })
      },
      loadUser() {
        patchState(store, { isLoading: true })
        return users$.getUser().pipe(
          tap({
            next: (user) => {
              // if backend doesn't persist unit prefs, merge local fallback (local wins only when server lacks the field)
              try {
                const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY)
                if (raw) {
                  const prefs = JSON.parse(raw) as Partial<User>
                  const merged = {
                    ...user,
                    temperatureUnit: user.temperatureUnit ?? prefs.temperatureUnit,
                    measurementSystem: user.measurementSystem ?? prefs.measurementSystem,
                    windSpeedUnit: normalizeWindSpeedUnit(
                      user.windSpeedUnit ?? prefs.windSpeedUnit,
                    ),
                    chartVisibleVariables:
                      user.chartVisibleVariables &&
                      user.chartVisibleVariables.length > 0
                        ? normalizeChartVisibleVariables(user.chartVisibleVariables)
                        : normalizeChartVisibleVariables(prefs.chartVisibleVariables),
                  }
                  patchState(store, { user: merged })
                  return
                }
              } catch (_e) {
                /* ignore localStorage errors */
              }

              patchState(store, {
                user: {
                  ...user,
                  windSpeedUnit: normalizeWindSpeedUnit(user.windSpeedUnit),
                  chartVisibleVariables: normalizeChartVisibleVariables(
                    user.chartVisibleVariables,
                  ),
                },
              })
            },
            error: (error) => {
              console.error('err companies store', error)
              patchState(store, { error })
            },
            complete: () =>
              patchState(store, { isLoading: false, initial: false }),
          }),
        )
      },
      updatePushNotifications(): void {
        if (!store.user()) return
        patchState(store, { isLoading: true })
        const u = { ...store.user()! }
        u.acceptPushNotifications = !u.acceptPushNotifications
        users$.patchUser(u).subscribe({
          next: (nu) => {
            patchState(store, {
              user: nu,
            })
            toastr$.success(
              translate.instant('TOAST.SUCCESS'),
              translate.instant('USER.NOTIFICATIONS.PREFERENCES_UPDATED'),
            )
          },
          error: (error) => {
            console.error('err companies store', error)
            patchState(store, { error })
            toastr$.error(
              translate.instant('USER.NOTIFICATIONS.PREFERENCES_NOT_UPDATED'),
              translate.instant('TOAST.ERROR'),
            )
          },
          complete: () =>
            patchState(store, { isLoading: false, initial: false }),
        })
      },

      updateTemperatureUnit(unit: 'C' | 'F'): void {
        if (!store.user()) return

        const prev = { ...store.user()! }
        // optimistic update so the UI reflects immediately
        patchState(store, { user: { ...prev, temperatureUnit: unit } })
        try {
          const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY)
          const prefs = raw ? JSON.parse(raw) : {}
          localStorage.setItem(
            USER_PREFERENCES_STORAGE_KEY,
            JSON.stringify({ ...prefs, temperatureUnit: unit }),
          )
        } catch (_e) {
          /* ignore localStorage */
        }

        const u = { ...store.user()! }
        users$.patchUser(u).subscribe({
          next: (nu) => {
            if (nu.temperatureUnit === unit) {
              // server persisted — accept canonical response
              patchState(store, { user: nu })
              toastr$.success(
                translate.instant('TOAST.SUCCESS'),
                translate.instant('USER.UNITS.PREFERENCES_UPDATED'),
              )
            } else {
              // server didn't persist the field — keep local preference and inform the user
              patchState(store, { user: { ...store.user()!, temperatureUnit: unit } })
              toastr$.info(
                translate.instant('USER.UNITS.SAVED_LOCALLY'),
                translate.instant('TOAST.INFO'),
              )
            }
          },
          error: (error) => {
            console.warn('err update temperature unit', error)
            // keep optimistic value locally and save fallback in localStorage
            patchState(store, { user: { ...store.user()!, temperatureUnit: unit }, error })
            toastr$.info(
              translate.instant('USER.UNITS.SAVED_LOCALLY_OFFLINE'),
              translate.instant('TOAST.INFO'),
            )
          },
          complete: () => patchState(store, { isLoading: false, initial: false }),
        })
      },

      updateMeasurementSystem(system: 'metric' | 'imperial'): void {
        if (!store.user()) return

        const prev = { ...store.user()! }
        // optimistic update
        patchState(store, { user: { ...prev, measurementSystem: system } })
        try {
          const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY)
          const prefs = raw ? JSON.parse(raw) : {}
          localStorage.setItem(
            USER_PREFERENCES_STORAGE_KEY,
            JSON.stringify({ ...prefs, measurementSystem: system }),
          )
        } catch (_e) {
          /* ignore localStorage */
        }

        const u = { ...store.user()! }
        users$.patchUser(u).subscribe({
          next: (nu) => {
            if (nu.measurementSystem === system) {
              patchState(store, { user: nu })
              toastr$.success(
                translate.instant('TOAST.SUCCESS'),
                translate.instant('USER.UNITS.PREFERENCES_UPDATED'),
              )
            } else {
              // keep local preference when server ignores the field
              patchState(store, { user: { ...store.user()!, measurementSystem: system } })
              toastr$.info(
                translate.instant('USER.UNITS.SAVED_LOCALLY'),
                translate.instant('TOAST.INFO'),
              )
            }
          },
          error: (error) => {
            console.warn('err update measurement system', error)
            patchState(store, { user: { ...store.user()!, measurementSystem: system }, error })
            toastr$.info(
              translate.instant('USER.UNITS.SAVED_LOCALLY_OFFLINE'),
              translate.instant('TOAST.INFO'),
            )
          },
          complete: () => patchState(store, { isLoading: false, initial: false }),
        })
      },

      updateWindSpeedUnit(unit: WindSpeedUnitPreference): void {
        if (!store.user()) return

        const prev = { ...store.user()! }
        patchState(store, { user: { ...prev, windSpeedUnit: unit } })
        try {
          const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY)
          const prefs = raw ? JSON.parse(raw) : {}
          localStorage.setItem(
            USER_PREFERENCES_STORAGE_KEY,
            JSON.stringify({ ...prefs, windSpeedUnit: unit }),
          )
        } catch (_e) {
          /* ignore localStorage */
        }

        const u = { ...store.user()! }
        users$.patchUser(u).subscribe({
          next: (nu) => {
            if (nu.windSpeedUnit === unit) {
              patchState(store, { user: nu })
              toastr$.success(
                translate.instant('TOAST.SUCCESS'),
                translate.instant('USER.UNITS.PREFERENCES_UPDATED'),
              )
            } else {
              patchState(store, { user: { ...store.user()!, windSpeedUnit: unit } })
              toastr$.info(
                translate.instant('USER.UNITS.SAVED_LOCALLY'),
                translate.instant('TOAST.INFO'),
              )
            }
          },
          error: (error) => {
            console.warn('err update wind speed unit', error)
            patchState(store, { user: { ...store.user()!, windSpeedUnit: unit }, error })
            toastr$.info(
              translate.instant('USER.UNITS.SAVED_LOCALLY_OFFLINE'),
              translate.instant('TOAST.INFO'),
            )
          },
          complete: () => patchState(store, { isLoading: false, initial: false }),
        })
      },

      updateChartVisibleVariables(variables: ChartVariablePreference[]): void {
        if (!store.user()) return

        const normalized = normalizeChartVisibleVariables(variables)
        const prev = { ...store.user()! }

        // optimistic update
        patchState(store, {
          user: { ...prev, chartVisibleVariables: normalized },
        })

        try {
          const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY)
          const prefs = raw ? JSON.parse(raw) : {}
          localStorage.setItem(
            USER_PREFERENCES_STORAGE_KEY,
            JSON.stringify({ ...prefs, chartVisibleVariables: normalized }),
          )
        } catch (_e) {
          /* ignore localStorage */
        }

        const u = { ...store.user()! }
        users$.patchUser(u).subscribe({
          next: (nu) => {
            if (
              nu.chartVisibleVariables &&
              nu.chartVisibleVariables.length > 0
            ) {
              patchState(store, {
                user: {
                  ...nu,
                  chartVisibleVariables: normalizeChartVisibleVariables(
                    nu.chartVisibleVariables,
                  ),
                },
              })
              toastr$.success(
                translate.instant('TOAST.SUCCESS'),
                translate.instant('USER.CHART.PREFERENCES_UPDATED'),
              )
            } else {
              // keep local preference when server ignores the field
              patchState(store, {
                user: {
                  ...store.user()!,
                  chartVisibleVariables: normalized,
                },
              })
              toastr$.info(
                translate.instant('USER.CHART.SAVED_LOCALLY'),
                translate.instant('TOAST.INFO'),
              )
            }
          },
          error: (error) => {
            console.warn('err update chart visible variables', error)
            patchState(store, {
              user: {
                ...store.user()!,
                chartVisibleVariables: normalized,
              },
              error,
            })
            toastr$.info(
              translate.instant('USER.CHART.SAVED_LOCALLY_OFFLINE'),
              translate.instant('TOAST.INFO'),
            )
          },
          complete: () => patchState(store, { isLoading: false, initial: false }),
        })
      },
    }),
  ),
)
